// apps/web/src/components/AuthLock.tsx
import * as React from 'react';
import { Lock, LogIn } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useLocale } from '../context/LocaleContext';

type Props = {
  /** Content to show (will be dimmed/disabled if user is not logged in). */
  children: React.ReactNode;
  /** Optional reason to show in the overlay. */
  label?: string;
  /** If true, don't visually dim children-just block clicks with overlay. */
  keepAppearance?: boolean;
  /** Optional className for layout hooks. */
  className?: string;
};

/**
 * Shows children normally if authenticated.
 * If not authenticated, renders a subtle lock overlay and blocks interaction.
 */
export function AuthLock({
  children,
  label = 'Login to use this feature',
  keepAppearance = false,
  className,
}: Props) {
  const { session } = useAuth();
  const { t } = useLocale();
  const authed = !!session;

  // Clicking "Login" opens the auth modal via a global event
  const openAuth = React.useCallback(() => {
    window.dispatchEvent(new CustomEvent('auth:open'));
  }, []);

  const rootClass = ['relative auth-lock', className].filter(Boolean).join(' ');
  const bodyClass = [
    'auth-lock-body',
    authed ? '' : (keepAppearance ? 'pointer-events-none' : 'opacity-50 pointer-events-none'),
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      {/* Content (dimmed and not interactive when locked) */}
      <div className={bodyClass}>
        {children}
      </div>

      {/* Overlay shown only when locked */}
      {!authed && (
        <div className="absolute inset-0 grid place-items-center">
          <div className="rounded-xl backdrop-blur-sm bg-white/70 dark:bg-gray-900/70 border border-gray-200/60 dark:border-gray-800/60 p-3 flex items-center gap-2 shadow">
            <Lock size={16} className="opacity-70" />
            <span className="text-sm">{label || t('Login to use this feature')}</span>
            <button
              className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
              onClick={openAuth}
            >
              <LogIn size={14} /> {t('Login')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
